/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { calcTargetValue, isOutSpec } from '../../detailUtils';
import useStyles from '../useStyles';
import Typography from '../../../../components/Typography/Typography';
import Grid from '../../../../components/Grid/Grid';
import SinteringDetailResultMetadata from './SinteringDetailResultMetadata';

/**
 * SizeDetail 元件顯示燒結尺寸檢驗的詳細資料，包含規格配置、量測數據與檢驗結果。
 *
 * @component
 * @param {Object} props - 元件屬性
 * @param {Array} props.sinteringSize - 燒結尺寸量測資料陣列
 * @param {Object} props.sinteringSize[].sinterRowId - 燒結行ID
 * @param {number} props.sinteringSize[].checkId - 檢查ID
 * @param {Array} props.sinteringSize[].specItems - 規格項目陣列
 * @param {string} props.sinteringSize[].specItems[].specId - 規格ID
 * @param {number} props.sinteringSize[].specItems[].itemId - 項目ID
 * @param {string} props.sinteringSize[].specItems[].specRecipe - 規格配方
 * @param {string} props.sinteringSize[].specItems[].specValue - 規格數值
 * @param {Array} props.sizeSpecConfig - 尺寸規格配置陣列
 * @param {string} props.sizeSpecConfig[].specId - 規格ID
 * @param {number} props.sizeSpecConfig[].styleId - 樣式ID
 * @param {string} props.sizeSpecConfig[].specItem - 規格項目名稱
 * @param {string} props.sizeSpecConfig[].specRecipe - 規格配方
 * @param {string} props.sizeSpecConfig[].specTarget - 規格目標值
 * @param {string} props.sizeSpecConfig[].specUpper - 規格上限
 * @param {string} props.sizeSpecConfig[].specLower - 規格下限
 * @param {string} props.sizeSpecConfig[].limitUpper - 限制上限
 * @param {string} props.sizeSpecConfig[].limitLower - 限制下限
 * @param {string} props.sizeSpecConfig[].specBias - 規格偏差
 * @param {boolean} props.isLoading - 載入狀態指示器
 * @param {Object} props.resultMetadata - 結果元資料
 * @param {boolean} props.resultMetadata.isManualPassSize - 尺寸檢驗是否手動通過
 * @param {string} props.resultMetadata.sizeComment - 尺寸檢驗備註
 * @returns {JSX.Element}
 */

const SizeDetail = ({ sinteringSize, sizeSpecConfig, isLoading, resultMetadata }) => {
  const { classes } = useStyles();
  return (
    <>
      {(!sizeSpecConfig || !sinteringSize) && !isLoading && (
        <div style={{ marginTop: 50, textAlign: 'center' }}>
          <Typography variant="subtitle1">目前沒有尺寸資料</Typography>
        </div>
      )}
      {sizeSpecConfig && sinteringSize && (
        <>
          <Grid container className={classes.detail_header_row}>
            {sizeSpecConfig &&
              sizeSpecConfig.map((spec) => (
                <div
                  key={spec.specId}
                  className={`${classes.detail_header_column} ${classes.size_header_column}`}
                >
                  <Grid item className={classes.inspection_column}>
                    <Typography variant="subtitle1">{spec.specItem}</Typography>
                    <div className={classes.size_target_group}>
                      <Typography variant="body1" className={classes.size_target}>
                        {spec.specTarget}
                      </Typography>
                      <ul className={classes.size_spec_group}>
                        <li className={`${classes.size_spec} ${classes.size_spec_upper}`}>
                          +{parseFloat(spec.specUpper)}
                        </li>
                        <li className={`${classes.size_spec} ${classes.size_spec_lower}`}>
                          {parseFloat(spec.specUpper) > 0
                            ? `-${parseFloat(spec.specLower)}`
                            : spec.specLower}
                        </li>
                      </ul>
                    </div>
                  </Grid>
                  {spec.specBias && (
                    <Typography variant="body2" className={classes.size_bias}>
                      量測偏差： {spec.specBias}
                    </Typography>
                  )}
                </div>
              ))}

            <Grid item className={`${classes.detail_header_column} ${classes.size_defect}`}>
              <Typography variant="subtitle1">Defect Type</Typography>
            </Grid>
          </Grid>
          {sinteringSize &&
            sinteringSize.map((row) => (
              <Grid container className={classes.detail_row} key={row.sinterRowId}>
                {row.specItems.map((spec) => {
                  const targetSpec = sizeSpecConfig.find((item) => item.specId === spec.specId);
                  const [maxValue, minValue, upperLimit, lowerLimit] = calcTargetValue(targetSpec);
                  const [outSpec, outLimit] = isOutSpec(
                    spec.specValue,
                    maxValue,
                    minValue,
                    upperLimit,
                    lowerLimit
                  );
                  return (
                    <Grid
                      item
                      className={`${classes.detail_column} ${classes.inspection_column} ${
                        classes.inspection_value_cell
                      } ${outSpec ? classes.out_spec : ''} ${outLimit ? classes.out_limit : ''}`}
                      key={spec.itemId}
                    >
                      <Typography variant="body1">{spec.specValue || '-'}</Typography>
                    </Grid>
                  );
                })}
                <Grid item className={`${classes.detail_column} ${classes.size_defect}`}>
                  <Typography variant="body1">
                    {row.defectType.length !== 0
                      ? row.defectType.map((type) => type.detectName).join(', ')
                      : '-'}
                  </Typography>
                </Grid>
              </Grid>
            ))}
        </>
      )}
      {!isLoading && (
        <SinteringDetailResultMetadata
          resultMetadata={{
            isManualPass: resultMetadata.isManualPassSize,
            comment: resultMetadata.sizeComment,
          }}
        />
      )}
    </>
  );
};

SizeDetail.propTypes = {
  sizeSpecConfig: PropTypes.array.isRequired,
  sinteringSize: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  resultMetadata: PropTypes.object.isRequired,
};

export default SizeDetail;
